import { useProjectParams } from '@hooks/useProjectParams';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { ProjectContractTypeEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { deleteBimOrderGql } from './deleteBimOrderQueries';

export const useDeleteBimOrder = ({ workOrderId }: { workOrderId: number }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id: projectId, methodology } = useProjectParams();
    const { pathname } = useLocation();
    const { projectData, refreshProject } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [isSubmitted, setIsSubmitted] = useState(false);

    const deleteBimOrderVariables = useMemo(() => ({ workOrderId }), [workOrderId]);

    const isFrameProject = projectData?.contractType === ProjectContractTypeEnum.Frame;
    const orderDeleteBtnTitle = `Удалить ${isFrameProject ? 'Наряд-Заказ' : 'Расчет по Договору'} `;
    const orderDeleteErrTitle = `Удаление ${isFrameProject ? 'Наряд-Заказа' : 'Расчета по Договору'} `;
    const orderDeleteConfirmTitle = `Вы уверены, что хотите удалить текущий ${
        isFrameProject ? 'Наряд-Заказ' : 'Расчет по Договору'
    } `;

    const orderRemoveTooltipText = `${orderDeleteErrTitle} возможно только в статусе "На формировании"`;

    const [resetData] = useResetStoreData({
        requestId: 'deleteBimOrder',
    });
    const successCreatingHandler = useCallback(() => {
        resetData(undefined);
    }, [resetData]);
    const [deleteBimOrderData, deleteBimOrderStatus, deleteBimOrderErr, deleteBimOrderRequest] = useRequest({
        requestId: 'deleteBimOrder',
        query: deleteBimOrderGql,
        variables: deleteBimOrderVariables,
        withEffect: false,
        dependenciesUpdater: successCreatingHandler,
    });

    useEffect(() => {
        if (deleteBimOrderStatus === RequestStatus.SUCCESS && deleteBimOrderData?.bimRemoveWorkOrder?.result) {
            if (workOrderId && deleteBimOrderData?.bimRemoveWorkOrder?.id === workOrderId) {
                const newPathName = `/projects/${methodology}/${projectId}/contract`;

                if (pathname !== newPathName) {
                    navigate(newPathName);
                }

                refreshProject();
            }
        }
    }, [
        workOrderId,
        projectId,
        pathname,
        navigate,
        refreshProject,
        methodology,
        deleteBimOrderStatus,
        deleteBimOrderData?.bimRemoveWorkOrder?.result,
        deleteBimOrderData?.bimRemoveWorkOrder?.id,
    ]);

    const handleDeleteBimOrder = useCallback(() => {
        dispatch(deleteBimOrderRequest(deleteBimOrderVariables));
        setIsSubmitted(true);
    }, [deleteBimOrderRequest, deleteBimOrderVariables, dispatch]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: handleDeleteBimOrder,
        });

    const handleCloseErrorModal = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    const handleCreateReport = useCallback(() => {
        ExportCSVReport(deleteBimOrderData?.bimRemoveWorkOrder?.validation);
    }, [deleteBimOrderData?.bimRemoveWorkOrder?.validation]);

    const DeleteOrderErrorHandler = useMemo(
        () => (
            <ErrorHandlingModal
                titleText={orderDeleteErrTitle}
                isSubmitted={isSubmitted}
                statusRequest={deleteBimOrderStatus}
                dataRequest={deleteBimOrderData?.bimRemoveWorkOrder}
                errorsRequest={deleteBimOrderErr}
                handleCloseModalError={handleCloseErrorModal}
                handleCreateReport={handleCreateReport}
            />
        ),
        [
            deleteBimOrderData?.bimRemoveWorkOrder,
            deleteBimOrderErr,
            deleteBimOrderStatus,
            handleCloseErrorModal,
            handleCreateReport,
            isSubmitted,
            orderDeleteErrTitle,
        ],
    );
    const DeleteOrderConfirmDialog = useMemo(
        () => (
            <ConfirmationDialog
                textHeader={orderDeleteBtnTitle}
                textConfirmation={orderDeleteConfirmTitle}
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        ),
        [
            handleActionQuery,
            handleCloseConfirmationDialog,
            isOpenConfirmationDialog,
            orderDeleteBtnTitle,
            orderDeleteConfirmTitle,
        ],
    );

    const deleteBimOrderHandler = useCallback(() => {
        sendGpnAnalyticsEvent({
            componentId: 'rim_order delete button_pressed',
            projectId,
            contractId: projectData?.contractId,
            orderId: workOrderId,
            orderStatus: projectData?.contractNavigation?.data?.workOrderNavs?.find((w) => w.id === Number(workOrderId))
                ?.status,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        });

        handleOpenConfirmationDialog();
    }, [
        handleOpenConfirmationDialog,
        projectData?.contractId,
        projectData?.contractNavigation?.data?.workOrderNavs,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
        workOrderId,
    ]);

    return {
        orderDeleteBtnTitle,
        deleteBimOrderHandler,
        DeleteOrderErrorHandler,
        DeleteOrderConfirmDialog,
        orderRemoveTooltipText,
    };
};
